import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import { useCookies } from 'react-cookie';
import { matchPath, useLocation } from 'react-router-dom';

import { zIndex } from '@mirinae/react-ui';

import closeXSVG from '@explorer/components/assets/Manul-X.svg';
import { cookieDomain, membersPaths } from '@mirinae/shared/defines/paths';
import { getUserStatus } from '@mirinae/defines/datas';
import { ENABLE_PRICE_CHANGE_NOTICE } from '@mirinae/shared/defines/promoFlags';
import { useAppVersionStore, useAppVersionStoreMethods } from '@mirinae/shared/stores/appVersion';

/*
 *    Copyright © 2026, Mirinae Corp.
 *
 *    The one-time notice of the annual Pro price rise to $99.99/year (#74; plan in
 *    docs/price-change-popup-plan.md). One picture whose black band "Go Pro now" at the bottom opens
 *    the Pro purchase: Plans & Pricing on the web (same tab), the app's own store purchase in the
 *    iOS/Android apps (Apple and Google require it), as TryFreeButton does. The web picture states
 *    $99.99/year; the app picture states no price ("Local pricing shown in app"), because the stores
 *    charge in the customer's own currency.
 *
 *    Shown, once, to anyone whose subscription is not 'active' (the issue's rule), on every Explorer
 *    page but the landing page; not tied to the old 'landed' cookie, which only a visit to the landing
 *    page sets now. Decided on every render, not once on mount, and only once the user and whether
 *    this is the app are known, so a Pro subscriber is never shown it while the session is still
 *    loading and the app never shows the web picture first. Closing it or opening the purchase sets
 *    the 'priceChange2026Seen' cookie; an app purchase that could not be opened leaves it up to try
 *    again. Switched by ENABLE_PRICE_CHANGE_NOTICE (shared/defines/promoFlags.js).
 */

const SEEN = 'priceChange2026Seen';
const TEN_YEARS = 10 * 365 * 24 * 60 * 60;
// the landing page's two addresses (explorer/index.js mounts the Explorer routes at /explorer/* and *),
// matched as the router matches them: any case, any trailing slashes (MR !55 review)
const LANDING_PATHS = ['/landing', '/explorer/landing'];
const onLanding = pathname => LANDING_PATHS.some(path => matchPath({ path }, pathname));

// each picture's source size and the first row of its black "Go Pro now" band, measured on John's artwork
// of 2026-10-31 (the two differ); the box and the button are placed from these as fractions, so any
// resized copy of the same picture fits
const ART = {
    web: { width: 1097, height: 1374, bandTop: 1186 },
    app: { width: 1114, height: 1402, bandTop: 1189 },
};
// what a screen reader says for each picture
const ALT = {
    web: 'Pro price change coming soon. Subscribe before rates increase: Pro will be $99.99 a year, '
        + 'due to increased hosting and support costs.',
    app: 'Price update coming soon. Subscribe to Pro before the price rise. Local pricing is shown in the app. '
        + 'The update is due to increased hosting and support costs.',
};
// the box, at the artwork's own proportions: as large as fits (420px at most, 90% of the width, 85% of the height)
const boxSize = ({ width, height }) => ({
    width: `min(420px, 90vw, calc(85vh * ${width} / ${height}))`,
    aspectRatio: `${width} / ${height}`,
});
// the button, on the drawn band: from its first row to the bottom of the picture
const band = ({ height, bandTop }) => ({
    left: 0,
    right: 0,
    top: `${(bandTop / height) * 100}%`,
    bottom: 0,
});

// the store purchase in the apps; resolves false when it was not opened (no bridge, or the bridge
// threw or rejected). The Flutter handler answers later, so its answer is awaited (Codex P74-3-1);
// PaymentChannel.postMessage answers nothing, so for it true only means the message was handed over.
const openAppPurchase = async () => {
    try {
        if (window.PaymentChannel) {
            window.PaymentChannel.postMessage(true);
            return true;
        }
        if (window.flutter_inappwebview) {
            await window.flutter_inappwebview.callHandler('PaymentChannel', true);
            return true;
        }
    } catch (e) {
        console.log('PriceChangeNotice: app purchase not opened', e);
    }
    return false;
};

const PriceChangeNotice = ({ user }) => {
    const [cookies, setCookie] = useCookies([SEEN]);
    const isApp = useAppVersionStore(s => s.isApp);
    const isAppChecked = useAppVersionStore(s => s.isAppChecked);
    const isPricePlan3PlusEnabled = useAppVersionStore(s => s.isPricePlan3PlusEnabled);
    const { setVersionData } = useAppVersionStoreMethods();
    const location = useLocation();
    const [closed, setClosed] = useState(false);
    const box = useRef(null);
    const before = useRef(null);
    const busy = useRef(false); // an app purchase is being opened: further clicks wait for it

    const show = ENABLE_PRICE_CHANGE_NOTICE
        && !closed
        && !cookies[SEEN]
        && Boolean(user?.role)
        && isAppChecked
        && !onLanding(location.pathname)
        && getUserStatus(user) !== 'active';

    const close = () => {
        setCookie(SEEN, 'true', { path: '/', maxAge: TEN_YEARS, domain: cookieDomain });
        setClosed(true);
    };

    const purchase = async (e) => {
        e.preventDefault();
        if (busy.current) return;
        if (!isApp) {
            close();
            window.location.assign(membersPaths.plan);
        } else if (!isPricePlan3PlusEnabled) {
            close();
            setVersionData({ displayMustUpgradeApp: 'pp3-explain-subscribe' }); // "please update the app"
        } else {
            busy.current = true;
            const opened = await openAppPurchase();
            busy.current = false;
            if (opened) close(); // else: the store did not open; stay up so they can try again
        }
    };

    // focus into the notice while it is up, back to where it was after; Esc closes, Tab stays inside.
    // The page may take focus after the notice opens (the analyzer's text box focuses itself): focus
    // that lands outside is brought back, and that element is where it goes once the notice closes.
    useEffect(() => {
        if (!show) return undefined;
        before.current = document.activeElement;
        // the notice itself, not a button: a button's focus ring would show on the picture for everyone;
        // Tab from here goes to the button
        if (box.current) box.current.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') {
                close();
            } else if (e.key === 'Tab' && box.current) {
                const all = [...box.current.querySelectorAll('button')];
                const i = all.indexOf(document.activeElement);
                const next = e.shiftKey ? (i <= 0 ? all.length - 1 : i - 1) : (i + 1) % all.length;
                e.preventDefault();
                all[next].focus();
            }
        };
        const onFocus = (e) => {
            if (!box.current || box.current.contains(e.target)) return;
            before.current = e.target;
            box.current.focus();
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('focusin', onFocus);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('focusin', onFocus);
            if (before.current && before.current.focus) before.current.focus();
        };
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!show) return null;
    const kind = isApp ? 'app' : 'web';
    const picture = `/assets/explorer/price-change-notice-${kind}.jpg`;
    return (
        <Backdrop>
            <Notice ref={box} style={boxSize(ART[kind])} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Mirinae Pro price change">
                <img src={picture} alt={ALT[kind]} />
                <Spot type="button" style={band(ART[kind])} aria-label="Go Pro now" onClick={purchase} />
                <Close type="button" aria-label="Close" onClick={close} />
            </Notice>
        </Backdrop>
    );
};

export default PriceChangeNotice;

const Backdrop = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: ${zIndex.tutorial};
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.4);
`;

// the box's size comes from the picture (boxSize); the picture fills it and the button is placed in it
// by fractions, so it stays on the drawn band at any size. The rounded corners clip the artwork's own.
const Notice = styled.div`
    position: relative;
    outline: none;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    img {
        display: block;
        width: 100%;
        height: 100%;
        max-width: none;
    }
`;

const Spot = styled.button`
    position: absolute;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    &:focus-visible { outline: 3px solid #FFF; outline-offset: -3px; }
`;

const Close = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    background: url(${closeXSVG}) no-repeat 50% / cover;
    cursor: pointer;
    &:focus-visible { outline: 3px solid #FFF; outline-offset: 0; border-radius: 50%; }
`;
